import { confirmPasswordReset, getAuth } from 'firebase/auth'
import { authFactory, type IAuthService, type AuthUser, type AuthResult } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import { logActivity } from '@syscolect/application'
import { firebaseApp } from './firebase'

/** Same ceiling as app-kit `recoverFromStaleBuild` — slow network must not block logout. */
const LOGOUT_FLUSH_MS = 1_000

class AuthService {
  private get service(): IAuthService {
    // Get the auth service lazily after authFactory.configure() has been called in bootstrap.ts
    return authFactory.getAuthService();
  }

  getCurrentUser(): AuthUser | null {
    return this.service.getCurrentUser();
  }

  getIdToken(forceRefresh?: boolean): Promise<string | null> {
    return this.service.getIdToken(forceRefresh);
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return this.service.onAuthStateChanged(callback);
  }

  async login(email: string, password: string, rememberMe: boolean): Promise<AuthResult> {
    const started = Date.now();
    try {
      const result = await this.service.login(email, password, rememberMe);
      logActivity({
        action: 'session.login',
        summary: 'Entrou',
        durationMs: Date.now() - started,
      });
      return result;
    } catch (error) {
      const reason =
        typeof error === 'object' && error != null && 'code' in error
          ? String((error as { code: unknown }).code)
          : 'unknown';
      logActivity({
        action: 'session.login',
        summary: 'Falhou ao entrar',
        outcome: 'error',
        durationMs: Date.now() - started,
        attrs: { reason },
      });
      throw error;
    }
  }

  createUser(email: string, password: string): Promise<AuthResult> {
    return this.service.createUser(email, password);
  }

  async logout(): Promise<void> {
    logActivity({
      action: 'session.logout',
      summary: 'Saiu',
    });
    // Flush while the token is still valid; cap wait so logout cannot hang.
    await Promise.race([
      rtLogService.flush({ keepalive: true }),
      new Promise<void>((resolve) => {
        setTimeout(resolve, LOGOUT_FLUSH_MS);
      }),
    ]);
    return this.service.logout();
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return this.service.sendPasswordResetEmail(email);
  }

  /**
   * Step 2 of password reset — consumes the `oobCode` from the reset link.
   * Wired directly to Firebase until `IAuthService` gains this method in data-access.
   */
  confirmPasswordReset(oobCode: string, password: string): Promise<void> {
    return confirmPasswordReset(getAuth(firebaseApp), oobCode, password);
  }

  // Expose other methods as needed, or just the ones currently used
}

export const authService = new AuthService();
